import { useMemo, useRef } from 'react';

import { PresentationSlideWrapper } from '@/modules/presentation/clientPresentation/PresentationSlideWrapper';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { SlideHoverHaloRefs } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { DividerSlide } from '@/modules/slideEditor/dividerSlide/DividerSlide';
import { BrandedPreviewSlide } from '@/modules/slideEditor/shared/BrandedPreviewSlide';

import {
  formToSlideDesign,
  SLIDE_PREVIEW_BODY_TEXT,
} from './SlideConfigForm.constants';
import { SlideConfigFormShape } from './SlideConfigForm.types';
import { SlideOriginDragOverlay } from './SlideOriginDragOverlay';

/**
 * Live preview for the divider slide config form. `formToSlideDesign`
 * converts form values (including the `inheritCover` flag) into a prop
 * override that `useMergedSlideDesign` resolves: when inherit is on,
 * the divider renders the cover's values; when off, it uses its own.
 */
export function DividerSlideConfigPreview(formValues: SlideConfigFormShape) {
  const tenantSlideDesign = formToSlideDesign(formValues);
  const slideRef = useRef<HTMLDivElement | null>(null);

  // Refs to slide-internal hover targets — see CoverSlideConfigPreview
  // for the wiring story.
  const titleHaloTargetRef = useRef<HTMLDivElement | null>(null);
  const customLogoRef = useRef<HTMLDivElement | null>(null);
  const hoverHaloRefs = useMemo<SlideHoverHaloRefs>(
    () => ({
      titleHaloTarget: titleHaloTargetRef,
      customLogo: customLogoRef,
    }),
    []
  );

  // While the divider is inheriting the cover's design, every form
  // input is locked — keep the preview fully inert by leaving the
  // wrapper's default `pointerEvents: 'none'` in place. Once inherit is
  // off, switch to `'auto'` so the drag/pinch overlays receive events.
  const isInheriting = formValues.inheritCover;

  return (
    <PresentationSlideWrapper
      ref={slideRef}
      sx={{ pointerEvents: isInheriting ? 'none' : 'auto' }}
    >
      <DividerSlide
        Slide={BrandedPreviewSlide}
        dividerCopy="Section Title"
        customContent={SLIDE_PREVIEW_BODY_TEXT}
        slideId="divider-slide-config-preview"
        tenantSlideDesign={tenantSlideDesign}
        footer={<PresentationSlide.Footer />}
        hoverHaloRefs={hoverHaloRefs}
        slideOverlay={
          <SlideOriginDragOverlay
            slideRef={slideRef}
            hoverHaloRefs={hoverHaloRefs}
            disabled={isInheriting}
          />
        }
      />
    </PresentationSlideWrapper>
  );
}
